import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '../components/ui/dropdown-menu';
import { AlignJustify, Bell, LogOut, Moon, Settings, Sun, SunMoon, User, Wifi, WifiOff, LayoutGrid, CalendarClock, Gauge, Users, Activity } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { PerfilModal } from './PerfilModal';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import socketService from '../services/socketService';
import api from '../services/api';
import { authService } from '../services/authService';
import { instanciaService } from '../services/instanciaService';
import { APP_VERSION } from '../config/version';
import LanguageSwitcher from './LanguageSwitcher';

interface Instance {
  id: string;
  nome: string;
  statusConexao: string;
  ultimaConexao?: string;
  // Add other properties that might come from the API
  instanceId?: string;
  instanceName?: string;
  apiKey?: string;
  apiEndpoint?: string;
}

interface Notificacao {
  id: string;
  conteudo: string;
  tipo: string;
  lida: boolean;
  ticketId?: string;
  mensagemId?: string;
  dataCriacao: string;
}

interface IntervalTimerProps {
  interval: number;
  callback: () => void;
}

const IntervalTimer: React.FC<IntervalTimerProps> = ({ interval, callback }) => {
  useEffect(() => {
    const timer = setInterval(callback, interval);
    return () => clearInterval(timer);
  }, [interval, callback]);

  return null;
};

interface HeaderProps {
  titulo?: string;
  user: any;
  sidebarCollapsed?: boolean;
  setSidebarCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
  variant?: 'desktop' | 'mobile';
  className?: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001')?.replace(/\/api$/, '');

const Header: React.FC<HeaderProps> = ({ titulo, user, sidebarCollapsed = false, setSidebarCollapsed, variant = 'desktop', className = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openChange, setOpenChange] = useState(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [instance, setInstance] = useState<Instance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    // Disconnect socket first to stop all real-time updates
    try {
      socketService.disconnect();
    } catch (e) { /* ignore */ }
    authService.logout();
    // Use hard redirect to fully unmount all components and clear state
    window.location.href = '/login';
  };

  const getInitials = (name: string | null | undefined = '') => {
    return (name ?? '')
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  };

  const fetchInstances = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: any = await instanciaService.listar();

      if (response.data.instancias && response.data.instancias.length > 0) {
        // Map the API response to our Instance interface
        const instancesData = response.data.instancias.map((item: any) => ({
          id: item.id,
          nome: item.nome || item.instanceName || 'Instância sem nome',
          statusConexao: item.statusConexao || 'disconnected',
          ultimaConexao: item.ultimaConexao,
          instanceId: item.instanceId,
          instanceName: item.instanceName,
          apiKey: item.apiKey,
          apiEndpoint: item.apiEndpoint
        }));

        setInstances(instancesData);

        // Set the first instance as active by default, or use a saved preference
        const savedInstanceId = localStorage.getItem('selectedInstanceId');
        const defaultInstance = savedInstanceId
          ? instancesData.find((i: Instance) => i.id === savedInstanceId) || instancesData[0]
          : instancesData[0];

        setInstance(defaultInstance);
        if (defaultInstance && !savedInstanceId) {
          localStorage.setItem('selectedInstanceId', defaultInstance.id);
        }
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Erro ao carregar instâncias');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchInstanceStatus = useCallback(async (instanceId: string) => {
    try {
      const response: any = await instanciaService.verificarStatus(instanceId);

      setInstances(prev =>
        prev.map(i =>
          i.id === instanceId ? {
            ...i,
            statusConexao: response.data.statusConexao || response.data.status || 'disconnected',
            ultimaConexao: response.data.ultimaConexao || i.ultimaConexao
          } : i
        )
      );

      setInstance(prev =>
        prev?.id === instanceId ? {
          ...prev,
          statusConexao: response.data.statusConexao || response.data.status || 'disconnected',
          ultimaConexao: response.data.ultimaConexao || prev.ultimaConexao
        } : prev
      );
    } catch (error) {
      console.error('Error fetching instance status:', error);
      // Don't show error toast to avoid spam since this runs frequently
    }
  }, []);

  const handleInstanceSelect = useCallback((selectedInstance: Instance) => {
    setInstance(selectedInstance);
    localStorage.setItem('selectedInstanceId', selectedInstance.id);
    fetchInstanceStatus(selectedInstance.id);
  }, [fetchInstanceStatus]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  // Listen for custom event to refresh instances
  useEffect(() => {
    const handleRefreshInstances = () => {
      fetchInstances();
    };

    window.addEventListener('refreshInstances', handleRefreshInstances);

    return () => {
      window.removeEventListener('refreshInstances', handleRefreshInstances);
    };
  }, [fetchInstances]);

  // Notifications logic
  const fetchNotificacoes = useCallback(async () => {
    try {
      if (user?.id) {
        const response = await api.get('/notificacoes');
        setNotificacoes(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar notificações', error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotificacoes();
  }, [fetchNotificacoes]);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (socket) {
      const handleNovaNotificacao = (notificacao: Notificacao) => {
        setNotificacoes(prev => [notificacao, ...prev]);
        toast.info(notificacao.conteudo, {
          action: notificacao.ticketId ? {
            label: 'Ver',
            onClick: () => navigate(`/atendimentos?ticketId=${notificacao.ticketId}`)
          } : undefined
        });
      };
      socket.on('nova_notificacao', handleNovaNotificacao);
      return () => {
        socket.off('nova_notificacao', handleNovaNotificacao);
      };
    }
  }, [navigate]);

  const handleMarcarComoLida = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.put(`/notificacoes/${id}/ler`);
      setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida', error);
    }
  };

  const handleMarcarTodasComoLida = async () => {
    try {
      await api.put('/notificacoes/ler-todas');
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas', error);
    }
  };

  const notificacoesNaoLidas = notificacoes.filter(n => !n.lida).length;

  // Theme effect is now handled by ThemeProvider

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  if (variant === 'mobile') {
    return (
      <div className={`flex items-center ${className}`}>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:bg-slate-800 p-1 h-10 w-10 flex items-center justify-center">
              <Avatar className="h-8 w-8 cursor-pointer dark:bg-slate-800">
                <AvatarImage
                  src={user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : undefined}
                  alt={user?.nome}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (user?.avatarUrl && !user.avatarUrl.startsWith('http')) {
                      target.src = `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;
                    } else {
                      target.style.display = 'none';
                    }
                  }}
                />
                <AvatarFallback className="bg-gray-400 text-white text-sm">{getInitials(user?.nome)}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="dark:bg-slate-800 w-56">
            <div onClick={() => { setOpenChange(true); setDropdownOpen(false); }} className="cursor-pointer flex items-center p-2 bg-gray-100 dark:bg-slate-700 dark:text-white rounded-md mb-2 mx-1">
              <Avatar className="h-8 w-8 mr-2">
                <AvatarFallback className="bg-gray-400 text-white text-xs dark:text-white">{getInitials(user?.nome)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-semibold">{user?.nome}</span>
                <span className="text-xs text-gray-500">{user?.role}</span>
              </div>
            </div>
            
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-500">{t('header.appearance')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
              <DropdownMenuRadioItem value="light" className="cursor-pointer dark:text-gray-300 py-2">
                <Sun className="mr-2 h-4 w-4" /> {t('header.light')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" className="cursor-pointer dark:text-gray-300 py-2">
                <Moon className="mr-2 h-4 w-4" /> {t('header.dark')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-500">{t('language.select')}</DropdownMenuLabel>
            <div className="px-2 py-1">
              <LanguageSwitcher variant="inline" />
            </div>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-500">{t('header.instances')}</DropdownMenuLabel>
            {instances.length > 0 ? (
              instances.map((i) => (
                <DropdownMenuItem key={i.id} onClick={() => handleInstanceSelect(i)} className="py-2 cursor-pointer">
                  {i.statusConexao === 'connected' ? <Wifi className="text-green-600 mr-2 h-4 w-4" /> : <WifiOff className="text-orange-600 mr-2 h-4 w-4" />}
                  <span className="truncate flex-1">{i.nome}</span>
                  {instance?.id === i.id && <div className="h-2 w-2 bg-blue-500 rounded-full ml-2" />}
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled className="text-xs italic py-2">{t('header.noInstance')}</DropdownMenuItem>
            )}

            {user?.role !== 'SuperAdmin' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-gray-500">{t('header.notifications')}</DropdownMenuLabel>
                <DropdownMenuItem disabled className="text-xs italic py-2">{t('header.noNewNotifications')}</DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer py-2 text-red-500 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" /> {t('header.logout')}
            </DropdownMenuItem>
            <div className="px-2 pt-1 pb-0.5 text-center text-[10px] text-gray-400 dark:text-gray-500 select-none">
              v{APP_VERSION}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <PerfilModal isOpen={openChange} onOpenChange={setOpenChange} />
        
        {instances.length > 0 && (
          <IntervalTimer interval={30 * 1000} callback={() => instances.forEach((i) => fetchInstanceStatus(i.id))} />
        )}
      </div>
    );
  }

  return (
    <header className={`bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 py-3 px-6 flex items-center justify-between position-fixed ${className}`}>
      <div className="flex justify-between align-middle items-center gap-4">
        {/* Botão Collapse Sidebar */}
        {setSidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:text-gray-500"
            title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            onClick={() => setSidebarCollapsed((prev) => !prev)}
          >
            <AlignJustify className="m-2 h-4 w-4" />
          </Button>
        )}
        {/* <h1 className="text-xl font-semibold text-gray-800 m-0 p-0 dark:text-gray-400">{titulo}</h1> */}
      </div>

      <div className="flex items-center">

        {/* Botões de Ação */}
        <div className="flex space-x-1 text-gray-600 dark:text-gray-400 gap-4">
          {/* Botão Instância */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:text-gray-500">
                {theme === 'light' ? (
                  <Sun className="h-4 w-4" />
                ) : theme === 'dark' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <SunMoon className="h-4 w-4" />
                )}
                <span className="sr-only">{t('header.changeTheme')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                <DropdownMenuRadioItem value="light" className="cursor-pointer dark:text-gray-500">
                  <Sun className="mr-2 h-4 w-4" />
                  <span>{t('header.light')}</span>
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark" className="cursor-pointer dark:text-gray-500">
                  <Moon className="mr-2 h-4 w-4" />
                  <span>{t('header.dark')}</span>
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system" className="cursor-pointer dark:text-gray-500">
                  <SunMoon className="mr-2 h-4 w-4" />
                  <span>{t('header.system')}</span>
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <LanguageSwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full w-auto p-2"
                title={instance?.statusConexao === 'connected' ? t('header.instanceConnected') : t('header.instanceDisconnected')}
              >
                {instance?.statusConexao === 'connected' ? (
                  <Wifi className="text-green-600" size={20} />
                ) : (
                  <WifiOff className="text-orange-600" size={20} />
                )}
                {instances.find((i) => i.statusConexao !== 'connected') && (
                  <div className="h-2 w-2 bg-red-500 rounded-full animate-ping" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <DropdownMenuLabel className="dark:text-gray-500">{t('header.instances')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isLoading ? (
                <div className="px-2 py-1.5 text-sm dark:text-gray-500">{t('common.loading')}</div>
              ) : instances.length > 0 ? (
                instances.map((i) => (
                  <DropdownMenuItem
                    key={i.id}
                    className="flex items-center gap-2"
                    onClick={() => handleInstanceSelect(i)}
                  >
                    {i.statusConexao === 'connected' ? (
                      <Wifi className="text-green-600" size={20} />
                    ) : (
                      <WifiOff className="text-orange-600" size={20} />
                    )}
                    <div className="flex-1">
                      <div className="font-medium">{i.nome}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {i.statusConexao === 'connected' ? t('common.connected') : t('common.disconnected')}
                        {/* {i.numeroConectado && ` • ${i.numeroConectado}`} */}
                        {i.ultimaConexao && ` • ${new Date(i.ultimaConexao).toLocaleString()}`}
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-1.5 text-sm text-gray-500">{t('header.noInstanceFound')}</div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {instances.length > 0 && (
            <>
              <IntervalTimer
                interval={30 * 1000} // 30 segundos
                callback={() => {
                  instances.forEach((i) => fetchInstanceStatus(i.id));
                }}
              />
            </>
          )}

          {/* Botão Notificações */}
          {user?.role !== 'SuperAdmin' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:bg-slate-800" title={t('header.notifications')}>
                  <Bell size={20} />
                  {notificacoesNaoLidas > 0 && (
                    <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center">
                      {notificacoesNaoLidas}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="dark:bg-slate-800 w-80 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="dark:text-gray-500">{t('header.notifications')}</DropdownMenuLabel>
                  {notificacoesNaoLidas > 0 && (
                    <Button variant="ghost" size="sm" className="h-auto p-0 text-xs text-blue-500 hover:text-blue-700 hover:bg-transparent" onClick={handleMarcarTodasComoLida}>
                      {t('header.markAllRead')}
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notificacoes.length > 0 ? (
                  notificacoes.map(n => (
                    <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!n.lida ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => n.ticketId && navigate(`/dashboard?ticketId=${n.ticketId}${n.mensagemId ? `&mensagemId=${n.mensagemId}` : ''}`)}>
                      <div className="flex justify-between w-full">
                        <span className={`text-sm ${!n.lida ? 'font-semibold' : ''}`}>{n.conteudo}</span>
                      </div>
                      <div className="flex justify-between w-full items-center mt-1">
                        <span className="text-xs text-gray-500">{new Date(n.dataCriacao).toLocaleString()}</span>
                        {!n.lida && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-500" onClick={(e) => handleMarcarComoLida(n.id, e)}>
                            {t('header.markAsRead')}
                          </Button>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled className="text-xs text-gray-500 italic">
                    {t('header.noNewNotifications')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Botão Mais Opções */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-md dark:bg-slate-800 flex items-center gap-2 py-2 px-4" title={t('header.moreOptions')}>
                <div className="flex flex-col items-start mr-2">
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{user?.nome}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</span>
                </div>
                <Avatar className="h-8 w-8 cursor-pointer dark:bg-slate-800">
                  <AvatarImage
                    src={user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : undefined}
                    alt={user?.nome}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (user?.avatarUrl && !user.avatarUrl.startsWith('http')) {
                        target.src = `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;
                      } else {
                        // If it's already a full URL but failed to load, show fallback
                        target.style.display = 'none';
                      }
                    }}
                  />
                  <AvatarFallback className="bg-gray-400 text-white text-sm">{getInitials(user?.nome)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <div onClick={() => setOpenChange(true)} className="cursor-pointer flex items-center p-1 bg-gray-200 dark:bg-slate-700 dark:text-white rounded">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : undefined}
                    alt={user?.nome}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (user?.avatarUrl && !user.avatarUrl.startsWith('http')) {
                        target.src = `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;
                      } else {
                        // If it's already a full URL but failed to load, show fallback
                        target.style.display = 'none';
                      }
                    }}
                  />
                  <AvatarFallback className="bg-gray-400 text-white text-xs dark:text-white">{getInitials(user?.nome)}</AvatarFallback>
                </Avatar>
                <DropdownMenuLabel className="dark:text-gray-500 text-xs dark:text-white">{user?.nome}</DropdownMenuLabel>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="dark:text-gray-500">{t('header.options')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => setOpenChange(true)}>
                <User className="mr-2 h-4 w-4" />
                <span>{t('header.profile')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
                <>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/painel-operacional')}>
                    <Gauge className="mr-2 h-4 w-4" />
                    <span>{t('header.operationsPanel')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/diagnostico-entrega')}>
                    <Activity className="mr-2 h-4 w-4" />
                    <span>{t('header.deliveryDiagnostics')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/kanban')}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    <span>{t('header.kanban')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/grupos')}>
                    <Users className="mr-2 h-4 w-4" />
                    <span>{t('header.groups')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/disparo-automatico')}>
                <CalendarClock className="mr-2 h-4 w-4" />
                <span>{t('header.automaticDispatch')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => window.location.href = '/configuracoes'}>
                <Settings className="mr-2 h-4 w-4" />
                <span>{t('header.settings')}</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem onClick={() => alert('Minimizar não implementado')}>
                <Users className="mr-2 h-4 w-4" /> // Ícone placeholder
                <span>Minimizar Barra</span>
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('header.logout')}</span>
              </DropdownMenuItem>
              <div className="px-2 pt-1.5 pb-0.5 text-center text-[10px] text-gray-400 dark:text-gray-500 select-none">
                v{APP_VERSION}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <PerfilModal isOpen={openChange} onOpenChange={setOpenChange} />
        </div>
      </div>
    </header>
  );
};

export default Header;
